/**
 * Project Bobcat — per-user display preference for showing ungrouped nodes.
 *
 * Renamed from `agent-view-preferences.ts` 2026-09-23 when Agent View was
 * deleted: canvas is now this preference's only consumer (Agent View used to
 * share it — "one toggle, not two independently configured copies of 'show
 * me what's ungrouped'" — but canvas is what's left). The storage key
 * ITSELF is left unchanged (`ps-agent-view-show-ungrouped`) so an existing
 * local dev/demo preference isn't silently reset by this rename — only the
 * source-level names changed.
 *
 * Same shape as `auto-renumber-storage.ts` and `expanded-cell-view-storage.ts`:
 * a tiny pure module with no MobX/store imports, so the localStorage contract
 * is unit-testable in isolation and the store just holds the observable.
 *
 * ## Why localStorage rather than the user's profile record
 *
 * This is where Pro Studio already keeps per-user, per-device display toggles,
 * and it needs no schema change to demo. It is a display preference, not
 * solution data — the cost of it not following someone to another browser is a
 * card being shown or hidden. When the profile screen gains the real control
 * this module is the seam to repoint at it: the store, the view and the label
 * below all stay as they are.
 */

/**
 * Default OFF. Gary's call, 2026-09-05 (originally for Agent View, now
 * canvas's): the view is meant to read as the agent roles in the solution,
 * and a long tail of leftover nodes beside the real agents buries that.
 * Anyone who wants the full picture opts in.
 */
export const SHOW_UNGROUPED_NODES_STORAGE_KEY = 'ps-agent-view-show-ungrouped';

/**
 * The label for the profile setting that will drive this.
 *
 * Lives here so the eventual control and this storage key cannot drift apart.
 * NOTE for whoever builds that UI: move this string into
 * `packages/translations/locales/pro-studio/*` before rendering it — copy that
 * only exists at runtime is invisible to `TranslationKeys` and to the Agent
 * vocabulary overlay (`agent-vocabulary.ts`).
 */
export const SHOW_UNGROUPED_NODES_LABEL = 'Enable Ungrouped Nodes';

export function loadShowUngroupedNodes(): boolean {
  try {
    // Only an explicit 'true' opts in; unset or corrupt falls back to the
    // default. Written the same way round as `loadExpandedCellViewOpen`, whose
    // default is also off.
    return localStorage.getItem(SHOW_UNGROUPED_NODES_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function persistShowUngroupedNodes(value: boolean): void {
  try {
    localStorage.setItem(SHOW_UNGROUPED_NODES_STORAGE_KEY, String(value));
  } catch {
    // localStorage unavailable (private mode / denied / quota) — non-fatal;
    // the in-memory observable still drives this session.
  }
}
